import React, { useCallback, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import RTKQErrorCard from "../../../common/components/RTKQErrorCard";
import type { RTKQueryError } from "../../../common/components/RTKQErrorCard";
import type { RootState, AppDispatch } from "../../../app/store";
import {
  completeStep,
  goToNextStep,
  resetCheckout,
  setSelectedPaymentMethod,
  setCheckoutView,
  type CheckoutStep,
} from "../checkoutSlice";

// Component Imports
import BillingStepContainer from "./BillingStep/BillingStepContainer";
import ShippingStepContainer from "./ShippingStep/ShippingStepContainer";
import PaymentStepContainer from "./PaymentStep/PaymentStepContainer";
import CheckoutStepsContainer from "./CheckoutStepsComponent/CheckoutStepsContainer";
import CheckoutNavigationContainer from "./CheckoutNavigationComponent/CheckoutNavigationContainer";
import OrderSummaryContainer from "./OrderSummaryComponent/OrderSummaryContainer";
import EmptyCartMessage from "../../cart/components/cartPage/EmptyCartMessage";
import CheckoutErrorBanner from "./CheckoutErrorBanner";
import IyzicoIframeComponent from "./IyzicoIframeComponent"; // Import new iframe component

// API & Type Imports
import { useGetCartQuery } from "../../cart/api/cartApi";
import {
  useGetCitiesQuery,
  useGetCitiesWithDistrictsQuery,
  usePlaceOrderMutation,
  useInitiateIyzicoPaymentMutation,
} from "../api/checkoutApi";
import type { TransformedAddress } from "../../cart/types/cartApiTypes";
import type { ThemeVariables } from "../../../types";

const CheckoutPageContainer: React.FC = () => {
  const { t, ready: translationsReady } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    currentStep,
    selectedPaymentMethod,
    useBillingForShipping,
    checkoutView,
  } = useSelector((state: RootState) => state.checkout);

  const [iyzicoIframeUrl, setIyzicoIframeUrl] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  const {
    data: cartData,
    isLoading: isCartLoading,
    error: cartErrorData,
    isSuccess: isCartSuccess,
  } = useGetCartQuery();
  const {
    data: citiesData,
    isLoading: isCitiesLoading,
    error: citiesErrorData,
  } = useGetCitiesQuery();
  const {
    data: citiesWithDistrictsData,
    isLoading: isCitiesWithDistrictsLoading,
    error: citiesWithdistrictsErrorData,
  } = useGetCitiesWithDistrictsQuery(undefined, {
    skip: !citiesData || isCitiesLoading,
  });

  const [placeOrder, { isLoading: isPlacingBacsOrder }] =
    usePlaceOrderMutation();
  const [initiateIyzicoPayment, { isLoading: isInitiatingIyzico }] =
    useInitiateIyzicoPaymentMutation();

  const isProcessingAction = isPlacingBacsOrder || isInitiatingIyzico;

  const previousCartTotalRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isCartSuccess || !cartData) {
      return;
    }
    const currentCartTotalString = cartData.totals.formattedTotalPrice;
    if (
      previousCartTotalRef.current !== undefined &&
      currentCartTotalString !== previousCartTotalRef.current
    ) {
      if (selectedPaymentMethod !== null) {
        dispatch(setSelectedPaymentMethod(null));
        setCheckoutError(t("checkout.errors.cartUpdatedPaymentReset"));
      }
    }
    previousCartTotalRef.current = currentCartTotalString;
  }, [
    cartData?.totals?.formattedTotalPrice,
    isCartSuccess,
    selectedPaymentMethod,
    dispatch,
    t,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const orderStatus = queryParams.get("order_status");
    const reason = queryParams.get("reason");

    if (orderStatus === "fail") {
      let errorMessage = t("checkout.errors.paymentFailedGeneric");
      if (reason === "payment_incomplete") {
        errorMessage = t("checkout.errors.paymentIncomplete");
      } else if (reason === "security") {
        errorMessage = t("checkout.errors.securityError");
      }
      setCheckoutError(errorMessage);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, t]);

  const isBaseLoading =
    !translationsReady ||
    isCartLoading ||
    isCitiesLoading ||
    isCitiesWithDistrictsLoading;
  const dataFetchError =
    cartErrorData || citiesErrorData || citiesWithdistrictsErrorData;

  const handleStepComplete = useCallback(
    (step: CheckoutStep) => {
      dispatch(completeStep(step));
      if (step !== "payment") {
        dispatch(goToNextStep());
      }
    },
    [dispatch]
  );

  const prepareAddressesForApi = useCallback(():
    | {
        billingAddressToSend: TransformedAddress;
        shippingAddressToSend: TransformedAddress;
      }
    | false => {
    const originalBillingAddress = cartData?.billingAddress;
    const originalShippingAddress = cartData?.shippingAddress;
    if (!originalBillingAddress) {
      console.error("Billing address missing for API preparation.");
      return false;
    }
    const billingAddressToSend = { ...originalBillingAddress };
    if (!billingAddressToSend.postcode?.trim())
      billingAddressToSend.postcode = "11111";
    const sourceShippingAddress = useBillingForShipping
      ? originalBillingAddress
      : originalShippingAddress;
    if (!sourceShippingAddress) {
      console.error(
        "Could not determine source shipping address for API preparation."
      );
      return false;
    }
    const shippingAddressToSend = { ...sourceShippingAddress };
    if (!shippingAddressToSend.postcode?.trim())
      shippingAddressToSend.postcode = "11111";
    return {
      billingAddressToSend: billingAddressToSend as TransformedAddress,
      shippingAddressToSend: shippingAddressToSend as TransformedAddress,
    };
  }, [cartData, useBillingForShipping]);

  const handlePlaceIyzicoOrder = async () => {
    if (isProcessingAction || !cartData) return;
    setShowLoadingOverlay(true);
    setCheckoutError(null);

    try {
      const result = await initiateIyzicoPayment({ cart: cartData }).unwrap();
      if (result.status === "success" && result.paymentPageUrl) {
        const iframeUrl = result.paymentPageUrl + "&iframe=true";
        setIyzicoIframeUrl(iframeUrl);
        dispatch(setCheckoutView("paymentIframe"));
      } else {
        throw new Error("Failed to get Iyzico payment details.");
      }
    } catch (err: any) {
      setCheckoutError(
        err?.data?.message || t("checkout.errors.processFailed")
      );
    } finally {
      setShowLoadingOverlay(false);
    }
  };

  const handlePlaceBacsOrder = useCallback(async () => {
    if (isProcessingAction) return;
    setShowLoadingOverlay(true);
    setCheckoutError(null);

    const preparedAddresses = prepareAddressesForApi();
    if (!preparedAddresses) {
      setCheckoutError(t("checkout.errors.addressMissing"));
      setShowLoadingOverlay(false);
      return;
    }
    const themeVars = window.themeVariables as ThemeVariables | undefined;
    const orderReceivedPageUrl = themeVars?.orderPageUrl;
    if (!orderReceivedPageUrl) {
      setCheckoutError(t("checkout.errors.configError"));
      setShowLoadingOverlay(false);
      return;
    }

    try {
      const result = await placeOrder({
        billingAddress: preparedAddresses.billingAddressToSend,
        shippingAddress: preparedAddresses.shippingAddressToSend,
        paymentMethod: "bacs",
      }).unwrap();
      if (result.orderId && result.orderKey) {
        const confirmationUrl = new URL(orderReceivedPageUrl);
        confirmationUrl.searchParams.set("order_status", "success");
        confirmationUrl.searchParams.set("payment_method", "bacs");
        confirmationUrl.searchParams.set("order_id", String(result.orderId));
        confirmationUrl.searchParams.set("order_key", result.orderKey);
        dispatch(resetCheckout());
        window.location.href = confirmationUrl.toString();
      } else {
        throw new Error("Missing order data in response.");
      }
    } catch (err: any) {
      setCheckoutError(
        err?.data?.message || t("checkout.errors.processFailed")
      );
    } finally {
      setShowLoadingOverlay(false);
    }
  }, [isProcessingAction, prepareAddressesForApi, placeOrder, dispatch, t]);

  if (isBaseLoading && !isCartSuccess) {
    return (
      <div className="checkout-page-container checkout-loading">
        <h1 className="checkout-title-skeleton">
          <Skeleton width={200} height={30} />
        </h1>
        <div
          className="checkout-steps-skeleton"
          style={{ marginBottom: "2rem" }}
        >
          <Skeleton height={40} width={"80%"} style={{ margin: "0 auto" }} />
        </div>
        <div className="checkout-content-skeleton">
          <div className="checkout-form-skeleton">
            <Skeleton height={40} count={5} style={{ marginBottom: "1rem" }} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "1rem",
              }}
            >
              <Skeleton height={40} width={100} />
              <Skeleton height={40} width={100} />
            </div>
          </div>
          <div className="checkout-summary-skeleton">
            <Skeleton
              height={24}
              width={150}
              style={{ marginBottom: "1.5rem" }}
            />
            <Skeleton height={60} count={2} style={{ marginBottom: "1rem" }} />
            <Skeleton
              height={20}
              count={3}
              style={{ marginBottom: "0.5rem" }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (dataFetchError && !isCartSuccess) {
    return (
      <div className="checkout-page-container checkout-error">
        <h1>{t("checkout.title", "Checkout")}</h1>
        <RTKQErrorCard
          error={dataFetchError as RTKQueryError | null}
          title={t("cart.errors.loadFailed", "Failed to load checkout data")}
        />
      </div>
    );
  }

  if (isCartSuccess && (!cartData || cartData.itemsCount === 0)) {
    return (
      <div className="checkout-page-container checkout-empty">
        <h1>{t("checkout.title", "Checkout")}</h1>
        <EmptyCartMessage />
      </div>
    );
  }

  return (
    <div className="checkout-page-container">
      {showLoadingOverlay && (
        <div className="checkout-loading-overlay">
          <div className="checkout-loading-spinner" />
          <p>{t("checkout.errors.processingPaymentOverlay")}</p>
        </div>
      )}
      <h1>{t("checkout.title", "Checkout")}</h1>
      <CheckoutErrorBanner
        message={checkoutError}
        onDismiss={() => setCheckoutError(null)}
      />
      {checkoutView === "form" && <CheckoutStepsContainer />}
      <div className="checkout-content">
        <div className="checkout-form-column">
          {checkoutView === "form" ? (
            <>
              {currentStep === "billing" && (
                <BillingStepContainer
                  onComplete={() => handleStepComplete("billing")}
                  cities={citiesData}
                  citiesWithDistricts={citiesWithDistrictsData}
                />
              )}
              {currentStep === "shipping" && (
                <ShippingStepContainer
                  onComplete={() => handleStepComplete("shipping")}
                  cities={citiesData}
                  citiesWithDistricts={citiesWithDistrictsData}
                />
              )}
              {currentStep === "payment" && (
                <PaymentStepContainer
                  availablePaymentMethods={cartData?.paymentMethods || []}
                  isLoading={isCartLoading}
                />
              )}
              <CheckoutNavigationContainer
                onPlaceBacsOrder={handlePlaceBacsOrder}
                onPlaceIyzicoOrder={handlePlaceIyzicoOrder}
                isPlacingOrder={isProcessingAction}
              />
            </>
          ) : (
            iyzicoIframeUrl && (
              <IyzicoIframeComponent
                iframeUrl={iyzicoIframeUrl}
                onGoBack={() => dispatch(setCheckoutView("form"))}
              />
            )
          )}
        </div>
        <div className="checkout-summary-column">
          <OrderSummaryContainer />
        </div>
      </div>
    </div>
  );
};

export default CheckoutPageContainer;
